import React from 'react';
import { AxiosResponse } from 'axios';
import { SidebarAd } from 'googlead/components/SidebarAd';
import { useMustacheTranslation, useOTSTranslation } from 'i18n/useOTSTranslation';
import { WEATHER_PAGE_SIZE } from 'ots-constants';
import {
  fetchRegionFeatured, fetchRegionMeta,
  fetchRegionOpeningDate, fetchRegionRelated, fetchRegionRelatedNews,
  fetchRegionResortsByStatus, fetchRegionResortListGuide,
} from 'api/region/static';
import {
  fetchResortDescriptionTabs,
  fetchResortSnowReport,
} from 'api/resort/static';
import { IRegionResortsPaginated, IRegionShort, IRegionFeaturedResort } from 'api/region/types';
import {
  IRelatedNews, ResortStatus, IResortPathInfo, IDescriptionsResort, IResortPageSnowInfo,
} from 'api/resort/types';
import getDailyFeaturedResortIndex from 'util/getDailyFeaturedResortIndex';
import { IFeaturedResort } from 'api/home/types';
import { CurrentLink, CurrentTab } from 'components/region/RegionHeaderNav/constants';
import { WeatherReports } from 'components/region/WeatherReports';
import { BoxWithShadow } from 'components/blocks/BoxWithShadow';
import { HeaderNavCurLink } from 'components/common/HeaderNav';
import { BlockContent } from 'components/blocks/BlockContent';
import { BlockHeader } from 'components/blocks/BlockHeader';
import { Main } from 'components/resort/Layout/Main';
import { Side } from 'components/resort/Layout/Side';
import { useRegionLayoutBubble } from 'page-components/region/_context';
import { IRegionLayout } from 'page-components/region/_layout';
import { RouteAndAnalyticsProps } from 'page-components/pathTypes';
import { getRegionSlug } from 'util/memcache';

import PlanYourTrip from 'components/common/PlanYourTrip/RegionWidget';
import { FeaturedResort } from 'components/home/FeaturedResort';
import { Hero400Error } from 'components/400';
import { OpenDatesTable } from 'components/region/OpenDatesTable';
import NoResortsBlock from 'components/blocks/NoResortBlock';
import { formatDateFromStr, MINIMAL_DATE_FORMAT } from 'util/formatDate';
import { AiAssistantWidget } from 'components/common/AiAssistantWidget';
import { assertShouldBeNot404, wrapServerSideProps, wrapStaticProps } from 'util/wrapProps';
import SurfaceRelatedArticles from 'components/resort/SurfaceRelatedArticles';

export type RegionOpenResortsPageProps = {
  resorts: IRegionResortsPaginated,
  openingDatesResorts: IRegionResortsPaginated | null,
  openingDate: string,
  numResorts: number,
  pathInfo: IRegionShort,
  relatedNews: IRelatedNews | null,
  featured: IRegionFeaturedResort,
  regionResortListGuide: Array<IResortPathInfo>,
  featuredResortByIndex: IResortPageSnowInfo | null,
  featuredResortDescriptionTabs?: Array<IDescriptionsResort> | null,
} & RouteAndAnalyticsProps & IRegionLayout;

export const DEBUG_TAG = 'region-open-resorts-page';
export const TEMPLATE_ID = '2.1.3';
export const TEMPLATE_NAME = 'Region Open Resorts';

export default function RegionOpenResortsPage({
  openingDate, resorts, openingDatesResorts, pathInfo, numResorts,
  relatedNews, regionResortListGuide, featuredResortDescriptionTabs,
  featuredResortByIndex,
}: RegionOpenResortsPageProps) {
  useRegionLayoutBubble({});
  const { t } = useOTSTranslation('common');
  const m = useMustacheTranslation('common');

  if (!resorts) {
    return <Hero400Error light />;
  }

  const featuredResort: IFeaturedResort | null = featuredResortByIndex ? {
    uuid: featuredResortByIndex.uuid,
    title: featuredResortByIndex.title,
    region: featuredResortByIndex.region,
    image: featuredResortByIndex.image,
    smallImage: featuredResortByIndex.smallImage ?? '',
    video: featuredResortByIndex.video ?? null,
    website: featuredResortByIndex.website ?? null,
    phone: featuredResortByIndex.phone ?? null,
    isPartner: featuredResortByIndex.isPartner,
    twitter: featuredResortByIndex.twitter ?? null,
    liftsUrl: featuredResortByIndex.liftsUrl ?? null,
    rentalUrl: featuredResortByIndex.rentalUrl ?? null,
    lessonsUrl: featuredResortByIndex.lessonsUrl ?? null,
    socialMediaAccounts: featuredResortByIndex.socialMediaAccounts ?? [],
  } : null;

  const seoTitle = pathInfo.isLogical ? t('region.openResorts.logicSeoTitle', { region: pathInfo.title })
    : t('region.openResorts.seoTitle', { region: pathInfo.title });

  const seoText = pathInfo.isLogical ? m('region.openResorts.logicSeoText', {
    region: pathInfo.title,
    numresorts: numResorts,
    firstopendate: formatDateFromStr(openingDate, MINIMAL_DATE_FORMAT),
  }) : m('region.openResorts.seoText', {
    region: pathInfo.title,
    numresorts: numResorts,
    firstopendate: formatDateFromStr(openingDate, MINIMAL_DATE_FORMAT),
  });

  return (
    <>
      <Main>
        <BoxWithShadow>
          <BlockHeader title={seoTitle} />
          <BlockContent>
            {seoText}
          </BlockContent>
        </BoxWithShadow>
        {!!resorts.pagination?.count && (
          <WeatherReports
            regionId={pathInfo.uuid}
            resorts={resorts}
            openFlag={ResortStatus.openResorts}
            isFresh
            useSkiResortLink
          />
        )}
        {!resorts.pagination?.count && (
          openingDatesResorts && !!openingDatesResorts.pagination?.count ? (
            <OpenDatesTable
              regionId={pathInfo.uuid}
              list={openingDatesResorts}
              hasMore={openingDatesResorts.pagination?.count > openingDatesResorts.pagination?.limit}
            />
          ) : (
            <NoResortsBlock text={t('region.openResorts.noResorts', { region: pathInfo.title })} />
          )
        )}
        <AiAssistantWidget
          templateId={TEMPLATE_ID}
          name={pathInfo.title}
          margin={{ marginTop: '0rem', marginBottom: '1.75rem' }}
        />
        <PlanYourTrip
          region={pathInfo}
          hasSkiRental={false}
          hasCarRental={false}
          hasFlights={false}
          isPartner={false}
        />
        {featuredResort && (
          <FeaturedResort
            resort={featuredResort}
            regionResorts={regionResortListGuide}
            resortDescriptionTabs={featuredResortDescriptionTabs ?? undefined}
            isOnRegionExceptSkiResorts
            utmCampaign="region_widget"
          />
        )}
      </Main>
      <Side>
        <SurfaceRelatedArticles
          relatedNewsList={relatedNews}
          isFresh
        />
        <SidebarAd isRegion id={pathInfo.uuid} showWeSki={false} />
      </Side>
    </>
  );
}

export const fetchProps = async ({ region }: { region: string }): Promise<RegionOpenResortsPageProps> => {
  const cache: AxiosResponse<IRegionShort> = await assertShouldBeNot404(() => getRegionSlug(region));
  const { uuid } = cache.data;
  const featured = await fetchRegionFeatured(uuid);

  const partnerResortsResponse = await fetchRegionResortsByStatus(
    uuid,
    ResortStatus.partner,
    1,
    100,
  );
  const partnerResorts = partnerResortsResponse?.data ?? [];

  let featuredResortByIndex: IResortPageSnowInfo | null = null;
  if (partnerResorts.length === 1) {
    featuredResortByIndex = await fetchResortSnowReport(partnerResorts[0].uuid);
  } else if (partnerResorts.length > 1) {
    const dailyIndex = getDailyFeaturedResortIndex(partnerResorts.length);
    featuredResortByIndex = await fetchResortSnowReport(partnerResorts[dailyIndex].uuid);
  }

  const openResorts = await fetchRegionResortsByStatus(uuid, ResortStatus.openResorts, 0, WEATHER_PAGE_SIZE);
  let openingDatesResorts: IRegionResortsPaginated | null = null;
  if (!openResorts.pagination?.count) {
    openingDatesResorts = await fetchRegionResortsByStatus(uuid, ResortStatus.openingDates, 0, WEATHER_PAGE_SIZE);
  }

  return {
    type: 'region',
    route: 'open-resorts',
    resorts: openResorts,
    openingDatesResorts,
    openingDate: await fetchRegionOpeningDate(uuid),
    featured,
    featuredResortByIndex,
    pathInfo: cache.data,
    numResorts: (await fetchRegionMeta(uuid))?.resorts?.active,
    relatedNews: await fetchRegionRelatedNews(uuid),
    regionResortListGuide: await fetchRegionResortListGuide(uuid),
    featuredResortDescriptionTabs: featuredResortByIndex?.uuid
      ? await fetchResortDescriptionTabs(featuredResortByIndex.uuid.toString())
      : null,

    /**
       * Layout
       */
    related: await fetchRegionRelated(uuid),
    navigation: {
      head: HeaderNavCurLink.SNOW_REPORTS,
      tab: CurrentTab.TRIP_PLANNING,
      link: CurrentLink.OPEN_RESORTS,
    },
    adTKey: 'metaregion open-resorts',
    templateName: TEMPLATE_NAME,
    templateId: TEMPLATE_ID,
    hero: {
      title: 'region.openResorts.heroTitle',
    },
    meta: {
      redirectTo: cache.data.domain ?? null,
      description: 'MetaRegion.open_resorts.description',
      title: 'MetaRegion.open_resorts.title',
    },
    prefetch: false,
  };
};

// This function gets called at build time
// eslint-disable-next-line max-len
export const getRegionOpenResortsPageStaticProps = wrapStaticProps(fetchProps);
export const getRegionOpenResortsPageServerSideProps = wrapServerSideProps(fetchProps);
